import React, { useState, useEffect } from 'react';
import { Box, Grid, Card, CardContent, Typography, Avatar, IconButton, Button, Paper, List, ListItem,
  ListItemText, ListItemAvatar, Chip, alpha, Skeleton, useMediaQuery, Badge,
  Stack, Fade, Slide, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Cake as CakeIcon,
  Receipt as ReceiptIcon,
  AccessTime as AccessTimeIcon,
  PersonAdd as PersonAddIcon,
  AccountBalance as AccountBalanceIcon,
  CreditCardOff as CreditCardOffIcon,
  Add as AddIcon,
  Error as ErrorIcon,
  ShowChart as ShowChartIcon,
  Notifications as NotificationsIcon,
  Visibility as ViewIcon,
  Support as SupportIcon,
  BugReport as BugReportIcon,
  Speed as SpeedIcon,
  PriorityHigh as PriorityHighIcon,
  Schedule as ScheduleIcon,
  TrendingFlat as TrendingFlatIcon,
  Help as HelpIcon,
  Build as BuildIcon,
  Warning as WarningIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import { Line, Area, Bar, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  ComposedChart, RadialBarChart, RadialBar } from 'recharts';
import { useTheme } from '@mui/material/styles';
import { CircularProgress } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import logService from '../../services/logService';
import { Log } from '../../types';
import { format } from 'date-fns';
import { clientService } from '../../services/clientService';
import { Client } from '../../types';
import { dashboardService } from '../../services/dashboardService';
import { dashboardPageSx, getChartThemeColors } from '../../styles/theme';

// Interfaces específicas para o dashboard de suporte
interface SuporteDashboardStats {
  totalClients: number;
  activeClients: number;
  inactiveClients: number;
  defaulters: number;
  totalRevenue: number;
  monthlyRevenue: number;
  pendingTickets: number;
  resolvedTickets: number;
  overdueInvoices: number;
  openTickets: number;
  inProgressTickets: number;
  resolvedToday: number;
  averageResolutionTime: number;
  customerSatisfaction: number;
}

interface KPICard {
  titulo: string;
  valor: string | number;
  variacao: number;
  icone: React.ReactElement;
  cor: string;
  gradiente: string;
  subtitulo?: string;
}

interface QuickInsight {
  titulo: string;
  valor: string | number;
  icone: React.ReactElement;
  cor: string;
  tendencia?: 'up' | 'down' | 'stable';
}

interface Aniversariante {
  id: number;
  nome: string;
  data: string;
  plano?: string;
}

interface NovoCliente {
  id: number;
  nome: string;
  cpf_cnpj: string;
  tipo_pessoa: string;
  status: string;
  cidade: string;
  estado: string;
  data_cadastro: string;
}

// Componente Hero Section para Suporte
const SuporteHeroSection: React.FC<{ 
  user: any; 
  stats: SuporteDashboardStats | null;
}> = ({ user, stats }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  return (
    <Card 
      elevation={3} 
      sx={{ 
        borderRadius: 4, 
        background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3,
        }
      }}
    >
      <CardContent sx={{ p: isMobile ? 3 : 4, position: 'relative', zIndex: 1 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SupportIcon sx={{ mr: 1, fontSize: 32 }} />
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold">
                {getGreeting()}, {user?.nome?.split(' ')[0]}! 👋
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
              Painel de Suporte - Gestão de Tickets e Atendimento
            </Typography>
            <Stack direction={isMobile ? "column" : "row"} spacing={3}>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {stats?.pendingTickets || 0}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Tickets Pendentes
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {stats?.resolvedToday || 0}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Resolvidos Hoje
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  {stats?.customerSatisfaction || 0}%
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Satisfação
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Avatar 
                sx={{ 
                  width: 80, 
                  height: 80, 
                  mx: 'auto', 
                  mb: 2,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  border: '3px solid rgba(255,255,255,0.3)'
                }}
              >
                <SupportIcon sx={{ fontSize: 40 }} />
              </Avatar>
              <Typography variant="h6" fontWeight="bold">
                Suporte
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Atendimento ao Cliente
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Componente KPI Card
const SuporteKPICard: React.FC<KPICard & { loading?: boolean; delay?: number }> = ({
  titulo,
  valor,
  variacao,
  icone,
  cor,
  gradiente,
  subtitulo,
  loading,
  delay = 0,
}) => {
  const theme = useTheme();

  return (
    <Slide direction="up" in timeout={800 + delay}>
      <Card 
        elevation={2} 
        sx={{ 
          borderRadius: 3,
          background: gradiente,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100px',
            height: '100px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '50%',
            transform: 'translate(30px, -30px)',
          }
        }}
      >
        <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ opacity: 0.8, mb: 0.5 }}>
                {titulo}
              </Typography>
              {loading ? (
                <Skeleton variant="text" width={80} height={40} />
              ) : (
                <Typography variant="h4" fontWeight="bold">
                  {valor}
                </Typography>
              )}
              {subtitulo && (
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {subtitulo}
                </Typography>
              )}
            </Box>
            <Box 
              sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {React.cloneElement(icone, { sx: { fontSize: 28 } })}
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {variacao > 0 ? (
              <TrendingUpIcon sx={{ fontSize: 16, color: '#4caf50' }} />
            ) : variacao < 0 ? (
              <TrendingDownIcon sx={{ fontSize: 16, color: '#f44336' }} />
            ) : (
              <TrendingFlatIcon sx={{ fontSize: 16, color: '#ff9800' }} />
            )}
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {variacao > 0 ? '+' : ''}{variacao}% vs mês anterior
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Slide>
  );
};

// Componente Quick Insight
const SuporteQuickInsightCard: React.FC<QuickInsight & { delay?: number }> = ({
  titulo,
  valor,
  icone,
  cor,
  tendencia,
  delay = 0,
}) => {
  const theme = useTheme();

  return (
    <Slide direction="up" in timeout={1000 + delay}>
      <Card elevation={1} sx={{ borderRadius: 2, border: `1px solid ${alpha(cor, 0.2)}` }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box 
              sx={{ 
                p: 1, 
                borderRadius: 1, 
                bgcolor: alpha(cor, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {React.cloneElement(icone, { sx: { fontSize: 20, color: cor } })}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {titulo}
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {valor}
              </Typography>
            </Box>
            {tendencia && (
              <Box>
                {tendencia === 'up' && <TrendingUpIcon sx={{ fontSize: 16, color: '#4caf50' }} />}
                {tendencia === 'down' && <TrendingDownIcon sx={{ fontSize: 16, color: '#f44336' }} />}
                {tendencia === 'stable' && <TrendingFlatIcon sx={{ fontSize: 16, color: '#ff9800' }} />}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Slide>
  );
};

// Componente Aniversariantes
const AniversariantesMes = React.forwardRef<HTMLDivElement, { aniversariantes: Aniversariante[] }>((props, ref) => {
  const theme = useTheme();
  const { aniversariantes } = props;
  return (
    <Card elevation={2} sx={{ borderRadius: 3 }} ref={ref}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CakeIcon sx={{ mr: 1, color: theme.palette.secondary.main }} />
          <Typography variant="h6" component="h2" fontWeight="bold">
            Aniversariantes do Mês
          </Typography>
          <Badge badgeContent={aniversariantes.length} color="secondary" sx={{ ml: 'auto' }} />
        </Box>
        <List sx={{ p: 0 }}>
          {aniversariantes.length === 0 && (
            <ListItem>
              <ListItemText primary="Nenhum aniversariante neste mês." />
            </ListItem>
          )}
          {aniversariantes.map((cliente, index) => (
            <ListItem
              key={cliente.id}
              sx={{
                px: 0,
                py: 1,
                borderBottom: index < aniversariantes.length - 1 ? 1 : 0,
                borderColor: 'divider',
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: theme.palette.secondary.light }}>
                  <CakeIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={cliente.nome}
                secondary={`${cliente.data}${cliente.plano ? ' - ' + cliente.plano : ''}`}
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
});

// Componente Novos Clientes
const NovosClientesMes = React.forwardRef<HTMLDivElement, { novosClientes: NovoCliente[] }>((props, ref) => {
  const theme = useTheme();
  const { novosClientes } = props;
  return (
    <Card elevation={2} sx={{ borderRadius: 3 }} ref={ref}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PersonAddIcon sx={{ mr: 1, color: theme.palette.success.main }} />
          <Typography variant="h6" component="h2" fontWeight="bold">
            Novos Clientes do Mês
          </Typography>
          <Badge badgeContent={novosClientes.length} color="success" sx={{ ml: 'auto' }} />
        </Box>
        <List sx={{ p: 0 }}>
          {novosClientes.length === 0 && (
            <ListItem>
              <ListItemText primary="Nenhum novo cliente neste mês." />
            </ListItem>
          )}
          {novosClientes.map((cliente, index) => (
            <ListItem
              key={cliente.id}
              sx={{
                px: 0,
                py: 1,
                borderBottom: index < novosClientes.length - 1 ? 1 : 0,
                borderColor: 'divider',
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: theme.palette.success.light }}>
                  <PersonAddIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={cliente.nome}
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {cliente.cpf_cnpj} • {cliente.tipo_pessoa.toUpperCase()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {cliente.cidade}/{cliente.estado} • {cliente.data_cadastro}
                    </Typography>
                    <Chip 
                      size="small" 
                      label={cliente.status} 
                      color={cliente.status === 'ativo' ? 'success' : 'default'}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
});

// Dashboard Principal do Suporte
const SuporteDashboard: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const chartColors = getChartThemeColors(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<SuporteDashboardStats | null>(null);
  const [logs, setLogs] = useState<Log[]>([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [logsFilters, setLogsFilters] = useState({ page: 1, limit: 8 });
  const [selectedLog, setSelectedLog] = useState<Log | null>(null);
  const [showLogDetails, setShowLogDetails] = useState(false);
  const [aniversariantes, setAniversariantes] = useState<Aniversariante[]>([]);
  const [novosClientes, setNovosClientes] = useState<NovoCliente[]>([]);
  const [performanceData, setPerformanceData] = useState<{ month: string, revenue: number, expenses: number }[]>([]);
  const [performanceLoading, setPerformanceLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1200));
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const response = await dashboardService.getStats();
        if (typeof response === 'object' && 'success' in response && response.success && 'data' in response) {
          const backendStats = response.data as any;
          setStats({
            totalClients: backendStats.totalClients || 0,
            activeClients: backendStats.activeClients || 0,
            inactiveClients: backendStats.inactiveClients || 0,
            defaulters: backendStats.defaulters || 0,
            totalRevenue: backendStats.totalRevenue || 0,
            monthlyRevenue: backendStats.monthlyRevenue || 0,
            pendingTickets: backendStats.pendingTickets || 0,
            resolvedTickets: backendStats.resolvedTickets || 0,
            overdueInvoices: backendStats.overdueInvoices || 0,
            openTickets: 15, // Mock - implementar API de tickets
            inProgressTickets: 8, // Mock - implementar API de tickets
            resolvedToday: 12, // Mock - implementar API de tickets
            averageResolutionTime: 4.5, // Mock - implementar API de tickets
            customerSatisfaction: 92, // Mock - implementar API de tickets
          });
        }
      } catch (err) {
        console.error('Erro ao carregar estatísticas do dashboard:', err);
      }
    };
    fetchDashboardStats();
  }, []);

  useEffect(() => {
    const fetchAniversariantes = async () => {
      try {
        const response = await clientService.getAniversariantesMes();
        if (response.success && response.data) {
          const clientes: Client[] = response.data;
          const aniversariantesDoMes = clientes.map(c => {
            const data = new Date(c.data_nascimento as any);
            return {
              id: c.id,
              nome: c.nome,
              data: data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
              plano: undefined
            };
          });
          setAniversariantes(aniversariantesDoMes);
        } else {
          setAniversariantes([]);
        }
      } catch (err) {
        console.error('Erro ao buscar aniversariantes:', err);
        setAniversariantes([]);
      }
    };
    fetchAniversariantes();
  }, []);

  useEffect(() => {
    const fetchNovosClientes = async () => {
      try {
        const response = await clientService.getNovosClientesMes();
        if (response.success && response.data) {
          const clientes: Client[] = response.data;
          const novosClientesDoMes = clientes.map(c => {
            const data = new Date(c.createdAt as any);
            return {
              id: c.id,
              nome: c.nome,
              cpf_cnpj: c.cpf_cnpj,
              tipo_pessoa: c.tipo_pessoa,
              status: c.status,
              cidade: c.cidade,
              estado: c.estado,
              data_cadastro: data.toLocaleDateString('pt-BR')
            };
          });
          setNovosClientes(novosClientesDoMes);
        } else {
          setNovosClientes([]);
        }
      } catch (err) {
        console.error('Erro ao buscar novos clientes:', err);
        setNovosClientes([]);
      }
    };
    fetchNovosClientes();
  }, []);

  useEffect(() => {
    const fetchPerformanceData = async () => {
      try {
        setPerformanceLoading(true);
        const response = await dashboardService.getPerformanceChart();
        if (typeof response === 'object' && 'success' in response && response.success && 'data' in response) {
          const data = response.data as { month: string, revenue: number, expenses: number }[];
          setPerformanceData(data);
        } else if (Array.isArray(response)) {
          setPerformanceData(response);
        } else {
          setPerformanceData([]);
        }
      } catch (err) {
        console.error('Erro ao buscar dados de performance:', err);
        setPerformanceData([]);
      } finally {
        setPerformanceLoading(false);
      }
    };
    fetchPerformanceData();
  }, []);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLogsLoading(true);
        const response = await logService.getLogs(logsFilters);
        if (response.success && response.data) {
          setLogs(response.data);
        } else {
          setLogs([]);
        }
      } catch (err: any) {
        console.error('Erro ao carregar logs:', err);
        setLogsError(err.message || 'Erro ao carregar logs');
        setLogs([]);
      } finally {
        setLogsLoading(false);
      }
    };
    fetchLogs();
  }, [logsFilters]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // KPIs específicos para suporte
  const suporteKpiCards: KPICard[] = [
    {
      titulo: 'Tickets Pendentes',
      valor: stats?.pendingTickets || 0,
      variacao: -12.5,
      icone: <AssignmentIcon />,
      cor: theme.palette.warning.main,
      gradiente: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
      subtitulo: 'Aguardando atendimento',
    },
    {
      titulo: 'Resolvidos Hoje',
      valor: stats?.resolvedToday || 0,
      variacao: 8.3,
      icone: <DoneIcon />,
      cor: theme.palette.success.main,
      gradiente: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
      subtitulo: 'Tickets finalizados',
    },
    {
      titulo: 'Tempo Médio',
      valor: `${stats?.averageResolutionTime || 0}h`,
      variacao: -5.2,
      icone: <SpeedIcon />,
      cor: theme.palette.info.main,
      gradiente: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
      subtitulo: 'Resolução',
    },
    {
      titulo: 'Satisfação',
      valor: `${stats?.customerSatisfaction || 0}%`,
      variacao: 2.1,
      icone: <HelpIcon />,
      cor: theme.palette.secondary.main,
      gradiente: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
      subtitulo: 'Cliente',
    },
  ];

  // Quick Insights específicos para suporte
  const suporteQuickInsights: QuickInsight[] = [
    {
      titulo: 'Em Andamento',
      valor: stats?.inProgressTickets || 0,
      icone: <BuildIcon />,
      cor: theme.palette.info.main,
      tendencia: 'stable',
    },
    {
      titulo: 'Urgentes',
      valor: 3,
      icone: <PriorityHighIcon />,
      cor: theme.palette.error.main,
      tendencia: 'down',
    },
    {
      titulo: 'Atrasados',
      valor: 2,
      icone: <WarningIcon />,
      cor: theme.palette.warning.main,
      tendencia: 'down',
    },
    {
      titulo: 'Novos Hoje',
      valor: 8,
      icone: <ScheduleIcon />,
      cor: theme.palette.primary.main,
      tendencia: 'up',
    },
  ];

  const handlePageChange = (event: unknown, newPage: number) => {
    setLogsFilters(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLogsFilters(prev => ({ ...prev, limit: parseInt(event.target.value, 10), page: 1 }));
  };

  const handleViewLogDetails = (log: Log) => {
    setSelectedLog(log);
    setShowLogDetails(true);
  };

  const formatJsonData = (data: any) => {
    if (!data) return 'Nenhum dado';
    return JSON.stringify(data, null, 2);
  };

  const logsPagination = {
    total: logs.length,
    page: 1,
    limit: logs.length
  };

  const logRowSx = { height: 32 };
  const logCellSx = { py: 0.25, px: 1 };

  return (
    <Box sx={dashboardPageSx}>
      {/* Hero Section */}
      <Box sx={{ mb: 4 }}>
        <SuporteHeroSection user={user} stats={stats} />
      </Box>

      {/* KPIs Principais */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {suporteKpiCards.map((kpi, index) => (
          <Grid item xs={12} sm={6} lg={3} key={index}>
            <SuporteKPICard {...kpi} loading={loading} delay={index * 100} />
          </Grid>
        ))}
      </Grid>

      {/* Quick Insights */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {suporteQuickInsights.map((insight, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <SuporteQuickInsightCard {...insight} delay={index * 50} />
          </Grid>
        ))}
      </Grid>

      {/* Gráfico de Performance e Listas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Gráfico de Performance */}
        <Grid item xs={12} lg={8}>
          <Fade in timeout={1000}>
            <Card elevation={2} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold">
                    📈 Performance de Atendimento
                  </Typography>
                  {performanceLoading && <CircularProgress size={24} />}
                </Box>
                <Box sx={{ height: 300 }}>
                  {performanceLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={performanceData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                        <XAxis
                          dataKey="month"
                          tick={{ fill: chartColors.axis }}
                          tickFormatter={(value) => value.slice(5, 7) + '/' + value.slice(0, 4)}
                        />
                        <YAxis tick={{ fill: chartColors.axis }} />
                        <RechartsTooltip 
                          formatter={(value: any) => [formatCurrency(value), '']}
                          labelFormatter={(label) => label.slice(5, 7) + '/' + label.slice(0, 4)}
                        />
                        <Legend />
                        <Bar dataKey="revenue" fill={theme.palette.success.main} name="Receitas" />
                        <Bar dataKey="expenses" fill={theme.palette.error.main} name="Despesas" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        {/* Lista de Aniversariantes */}
        <Grid item xs={12} lg={4}>
          <Fade in timeout={1200}>
            <AniversariantesMes aniversariantes={aniversariantes} />
          </Fade>
        </Grid>
      </Grid>

      {/* Novos Clientes e Atividades */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Lista de Novos Clientes */}
        <Grid item xs={12} lg={6}>
          <Fade in timeout={1300}>
            <NovosClientesMes novosClientes={novosClientes} />
          </Fade>
        </Grid>

        {/* Atividades Recentes */}
        <Grid item xs={12} lg={6}>
          <Fade in timeout={1400}>
            <Card elevation={2} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold">
                    🕒 Atividades de Suporte
                  </Typography>
                  <Badge badgeContent={logs.length} color="primary">
                    <NotificationsIcon />
                  </Badge>
                </Box>
                <Box sx={{ width: '100%', maxWidth: '100vw', overflowX: 'auto' }}>
                  <TableContainer sx={{ minWidth: 400, maxWidth: '100%', boxShadow: 'none' }}>
                    <Table size="small" sx={{ minWidth: 500, maxWidth: '100%' }}>
                      <TableHead>
                        <TableRow sx={logRowSx}>
                          <TableCell sx={{ ...logCellSx, whiteSpace: 'nowrap', fontSize: '0.95rem' }} width="12%">Ação</TableCell>
                          <TableCell sx={{ ...logCellSx, whiteSpace: 'nowrap', fontSize: '0.95rem' }} width="12%">Tipo</TableCell>
                          <TableCell sx={{ ...logCellSx, whiteSpace: 'nowrap', fontSize: '0.95rem' }} width="36%">Descrição</TableCell>
                          <TableCell sx={{ ...logCellSx, whiteSpace: 'nowrap', fontSize: '0.95rem' }} width="15%">Usuário</TableCell>
                          <TableCell sx={{ ...logCellSx, whiteSpace: 'nowrap', fontSize: '0.95rem' }} width="15%">Data</TableCell>
                          <TableCell align="center" sx={{ ...logCellSx, whiteSpace: 'nowrap', fontSize: '0.95rem' }} width="10%">Ações</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {logsLoading ? (
                          <TableRow sx={logRowSx}>
                            <TableCell colSpan={6} align="center" sx={logCellSx}>
                              <CircularProgress />
                            </TableCell>
                          </TableRow>
                        ) : logsError ? (
                          <TableRow sx={logRowSx}>
                            <TableCell colSpan={6} align="center" sx={logCellSx}>
                              <Alert severity="error">{logsError}</Alert>
                            </TableCell>
                          </TableRow>
                        ) : logs.length === 0 ? (
                          <TableRow sx={logRowSx}>
                            <TableCell colSpan={6} align="center" sx={logCellSx}>
                              Nenhum log encontrado
                            </TableCell>
                          </TableRow>
                        ) : (
                          logs.slice(0, 5).map((log: Log) => (
                            <TableRow hover key={log.id} sx={logRowSx}>
                              <TableCell sx={{ ...logCellSx, whiteSpace: 'nowrap', fontSize: '0.95rem' }}>
                                <Chip
                                  size="small"
                                  label={logService.formatLogType(log.tipo)}
                                  color={logService.getTypeColor(log.tipo) as any}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell sx={{ ...logCellSx, whiteSpace: 'nowrap', fontSize: '0.95rem' }}>
                                <Chip
                                  size="small"
                                  label={logService.formatLogAction(log.acao)}
                                  color={logService.getActionColor(log.acao) as any}
                                />
                              </TableCell>
                              <TableCell sx={{ ...logCellSx, whiteSpace: 'nowrap', fontSize: '0.95rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                <Typography variant="body2" noWrap>
                                  {log.descricao}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ ...logCellSx, whiteSpace: 'nowrap', fontSize: '0.95rem' }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <PeopleIcon fontSize="small" color="action" />
                                  <Typography variant="body2" noWrap>
                                    {log.usuario?.usuario || 'Sistema'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ ...logCellSx, whiteSpace: 'nowrap', fontSize: '0.95rem' }}>
                                <Typography variant="body2" noWrap>
                                  {format(new Date(log.createdAt), 'dd/MM HH:mm')}
                                </Typography>
                              </TableCell>
                              <TableCell align="center" sx={logCellSx}>
                                <Stack direction="row" spacing={0.5} justifyContent="center">
                                  <Tooltip title="Ver detalhes">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleViewLogDetails(log)}
                                    >
                                      <ViewIcon />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Grid>
      </Grid>

      {/* Quick Actions Button */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
        }}
      >
        <Fade in timeout={1500}>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            sx={{
              borderRadius: '25px',
              textTransform: 'none',
              boxShadow: theme.shadows[8],
              px: 3,
              '&:hover': {
                transform: 'scale(1.05)',
                transition: 'all 0.2s ease-in-out',
              },
            }}
          >
            Novo Ticket
          </Button>
        </Fade>
      </Box>

      {/* Dialog de Detalhes do Log */}
      <Dialog
        open={showLogDetails}
        onClose={() => setShowLogDetails(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detalhes do Log #{selectedLog?.id}
        </DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Informações Básicas
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography><strong>Tipo:</strong> {logService.formatLogType(selectedLog.tipo)}</Typography>
                  <Typography><strong>Ação:</strong> {logService.formatLogAction(selectedLog.acao)}</Typography>
                  <Typography><strong>Descrição:</strong> {selectedLog.descricao}</Typography>
                  <Typography><strong>Usuário:</strong> {selectedLog.usuario?.usuario || 'Sistema'}</Typography>
                  <Typography><strong>Data:</strong> {format(new Date(selectedLog.createdAt), 'dd/MM/yyyy HH:mm')}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Informações Adicionais
                </Typography>
                <Box sx={{ pl: 2 }}>
                  {selectedLog.entidade_tipo && (
                    <Typography variant="body2">
                      <strong>Entidade:</strong> {selectedLog.entidade_tipo} #{selectedLog.entidade_id}
                    </Typography>
                  )}
                  {selectedLog.ip_address && (
                    <Typography variant="body2">
                      <strong>IP:</strong> {selectedLog.ip_address}
                    </Typography>
                  )}
                  {selectedLog.user_agent && (
                    <Typography variant="body2" noWrap>
                      <strong>User Agent:</strong> {selectedLog.user_agent}
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                {selectedLog.metadata && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      Metadados
                    </Typography>
                    <Box sx={{ pl: 2 }}>
                      <pre style={{ fontSize: '0.75rem', overflow: 'auto' }}>
                        {formatJsonData(selectedLog.metadata)}
                      </pre>
                    </Box>
                  </>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLogDetails(false)} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuporteDashboard; 